import {createTheme, Grow} from "@mui/material";

const workSans = "'Work Sans', Roboto, sans-serif";
const spaceMono = "'Space Mono', monospace";

export type ThemeMode = "light" | "dark";

// Surface/border/shadow values per mode — kept in sync with the CSS tokens in
// App.css so MUI-rendered chrome (dialogs, buttons) matches the rest of the app.
const surfaces = {
    light: {
        paper: "#ffffff",
        background: "#f3f0e8", // Off-White
        border: "#2e2a26", // Soft Black
        text: "#2e2a26", // Soft Black
        textSecondary: "#817a6e", // Muted
        divider: "#adaba8", // Border Gray
        grape: "#5538c2", // Grape — white text safe
        grapeContrast: "#ffffff",
        shadow: "rgba(46, 42, 38, 0.18)",
    },
    dark: {
        paper: "#262019",
        background: "#1b1714",
        border: "#4a443c",
        text: "#ece7db",
        textSecondary: "#a39b8d",
        divider: "#4a453f",
        grape: "#9d84f6", // brighter Grape — needs dark text for contrast
        grapeContrast: "#17130f",
        shadow: "rgba(0, 0, 0, 0.5)",
    },
} as const;

export const buildTheme = (mode: ThemeMode = "light") => {
    const s = surfaces[mode];
    return createTheme({
    palette: {
        mode,
        primary: {
            main: s.grape, // Grape — brand lead
            contrastText: s.grapeContrast,
        },
        secondary: {
            main: "#108164", // Green
            contrastText: "#ffffff",
        },
        error: {
            main: mode === "dark" ? "#ff6b6b" : "#ff4d4d", // Watermelon
        },
        text: {
            primary: s.text,
            secondary: s.textSecondary,
        },
        divider: s.divider,
        background: {
            default: s.background,
            paper: s.paper,
        },
    },
    shape: {
        borderRadius: 10,
    },
    // Type scale — MJL brand Foundations (Figma: 2FBuOcSy6pBz3R42ddjmqm).
    // Headings/body: Work Sans. overline maps to the Space Mono "Mono Tab" style.
    typography: {
        fontFamily: workSans,
        h1: { fontFamily: workSans, fontWeight: 700, fontSize: "3.75rem", lineHeight: 1.1, letterSpacing: "-0.033em" },
        h2: { fontFamily: workSans, fontWeight: 700, fontSize: "2.75rem", lineHeight: 1.14, letterSpacing: "-0.034em" },
        h3: { fontFamily: workSans, fontWeight: 700, fontSize: "2.5rem", lineHeight: 1.16, letterSpacing: "-0.025em" },
        h4: { fontFamily: workSans, fontWeight: 700, fontSize: "2rem", lineHeight: 1.2, letterSpacing: "-0.016em" },
        h5: { fontFamily: workSans, fontWeight: 700, fontSize: "1.625rem", lineHeight: 1.24 },
        h6: { fontFamily: workSans, fontWeight: 700, fontSize: "1.25rem", lineHeight: 1.3 },
        body1: { fontFamily: workSans, fontWeight: 400, fontSize: "1.125rem", lineHeight: 1.44 }, // Body L
        body2: { fontFamily: workSans, fontWeight: 400, fontSize: "0.875rem", lineHeight: 1.57 }, // Body S
        subtitle1: { fontFamily: workSans, fontWeight: 400, fontSize: "1rem", lineHeight: 1.5 }, // Body M
        button: { fontFamily: workSans, fontWeight: 600, fontSize: "1rem", lineHeight: 1.125, letterSpacing: 0, textTransform: "none" }, // Button
        caption: { fontFamily: workSans, fontWeight: 700, fontSize: "0.8125rem", lineHeight: 1.3 }, // Label
        overline: { fontFamily: spaceMono, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1.17, letterSpacing: "0.167em", textTransform: "uppercase" }, // Mono Tab
    },
    components: {
        MuiDialog: {
            defaultProps: {
                // "Pop" the modal in with a scale/grow transition
                TransitionComponent: Grow,
            },
            styleOverrides: {
                paper: {
                    // Brand OS window (see Figma "Windows & OS")
                    borderRadius: 4,
                    border: `2px solid ${s.border}`,
                    boxShadow: `5px 5px 4px ${s.shadow}`,
                    backgroundColor: s.paper,
                    overflow: 'hidden',
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    backgroundColor: s.paper,
                }
            }
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 999, // pill — matches brand rounded shapes
                    paddingInline: '1.5rem',
                    transition: 'transform 0.15s ease, background-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    transition: 'transform 0.15s ease, background-color 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    },
                },
            },
        },
    }
    });
};

// Default (light) theme — retained for any non-mode-aware imports.
export const theme = buildTheme("light");
